import { createReducer, on } from '@ngrx/store';
import { AchActions, CheckActions, ConfirmationPageActions, InternalTransferActions, InternalTransfersActions, ReviewPageActions, TransactionFormActions, WireActions } from '../../create-request/actions';
import { CodeAndDescription } from '../../create-request/models/shared/CodeAndDescription'
import { StandingAuthAchActions, StandingAuthCheckActions, StandingAuthConfirmationPageActions, StandingAuthFormActions, StandingAuthInternalTransferActions, StandingAuthReviewPageActions, StandingAuthWireActions } from '../../standing-authorizations/actions';
import { WarningOrError } from '../../create-request/models/shared/WarningOrError';
import { DateTime } from 'luxon';
import { convertDate, getDateTime, getEasternTimeWithEtSuffix } from '../utilities/datetime-utils';

export const confirmationPageKey = 'confirmationPage';

export interface State {
  caseId: string | any;
  taxId: string;
  submitCreateCaseId: string;
  status: CodeAndDescription;
  requestedDate: string;
  requestedTime: string;
  viewTransactionStatusClicked: boolean;
  createAnotherRequestClicked: boolean;
  source: string;
  loginId?: string;
  initiatedBy?: string;
  createNewStandingAuthClicked: boolean;
  achErrorList: WarningOrError[];
  isCreatePdfFlow: boolean;
}

export const initialConfirmationPage: State = {
  caseId: '',
  taxId: '',
  submitCreateCaseId: '',
  status: { code: '', description: '' },
  requestedDate: '',
  requestedTime: '',
  viewTransactionStatusClicked: false,
  createAnotherRequestClicked: false,
  source: '',
  loginId: '',
  initiatedBy: '',
  createNewStandingAuthClicked: false,
  achErrorList: [],
  isCreatePdfFlow: false
};

export const confirmationPageReducer = createReducer(
  initialConfirmationPage,
  on(
    ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
    StandingAuthConfirmationPageActions.returnStandingAuthClicked,
    StandingAuthFormActions.cancelClicked,
    TransactionFormActions.cancelClicked,
    ReviewPageActions.cancelClicked,
    StandingAuthReviewPageActions.cancelClicked, () => {
      return initialConfirmationPage;
    }
  ),
  on(AchActions.callAcknowledge,
    AchActions.callAddProfileAcknowledge,
    InternalTransferActions.callAcknowledge,
    InternalTransfersActions.callAcknowledgeList,
    CheckActions.callOnetimeAcknowledge,
    CheckActions.callRecurringAcknowledge, (state) => {
      return {
        ...state,
        viewTransactionStatusClicked: false,
        createAnotherRequestClicked: false,
        caseId: '',
        submitCreateCaseId: '',
        initiatedBy: '',
        loginId: '',
        isCreatePdfFlow: false,
      }
    }),
  on(AchActions.acknowledgeCallSuccessful,
    StandingAuthAchActions.acknowledgeCallSuccessful, (state, response) => {
    return {
      ...state,
      caseId: '',
      taxId: response.response.taxId,
      status: response.response.status,
      requestedDate: response.response.requestedDate.slice(0, 10),
      requestedTime: response.response.requestedTime,
      source: response.response.source?.description,
      loginId: response.response.lastInitiatedBy,
      initiatedBy: response.response?.initiatedBySimLogonId,
      achErrorList: response.response.messageFields?.errorList,
      isCreatePdfFlow: false,
    };
  }),
  on(
    InternalTransferActions.acknowledgeCallSuccessful,
    StandingAuthInternalTransferActions.acknowledgeCallSuccessful, (state, response) => {
      return {
        ...state,
        caseId: response.response.caseId,
        taxId: response.response.taxId,
        submitCreateCaseId: response.response.submitAndCreateSloaCaseId,
        status: response.response.status,
        requestedDate: response.response.requestedDate,
        requestedTime: response.response.requestedTime,
        source: response.response.source.description,
        loginId: response.response.loginId,
        isCreatePdfFlow: false,
      };
    }),
  on(InternalTransferActions.inquiryCallSuccessful, (state, response) => {
    return {
      ...state,
      initiatedBy: response?.internalTransferInquiryResponse?.auditFields?.request?.user ?? ''
    };
  }),
  on(WireActions.acknowledgeCallV3Successful,
    StandingAuthWireActions.acknowledgeCallV3Successful, (state, response) => {
      const { requestDate, timeOfRequest } = response.transactionDetails || {};
      const { date, time } = getDateTime(requestDate);
      return {
        ...state,
        withholdFedTax: response.transactionDetails?.isWithholdFederalTaxes?.toLowerCase() === "yes",
        withholdStateTax: response.transactionDetails?.isWithholdStateTaxes?.toLowerCase() === "yes",
        caseId: response.transactionDetails?.caseId,
        taxId: response.fromDetails.taxIdSSN,
        submitCreateCaseId: response.transactionDetails?.submitCreateSloaCaseId,
        status: {
          code: '',
          description: response.transactionDetails?.status === 'Pending-Review' ? 'Pending' : response.transactionDetails?.status ,
        },
        requestedDate: convertDate(requestDate || date) ?? '',
        requestedTime: timeOfRequest || time,
        source: response.transactionDetails?.source ? response.transactionDetails.source: 'Schwabinstitutional.com',
        loginId: response.transactionDetails?.initiatedBy,
        isCreatePdfFlow: false,
      };
    }),
  on(CheckActions.acknowledgeCallSuccessful,
    StandingAuthCheckActions.acknowledgeCallSuccessful, (state, response) => {
    return {
      ...state,
      caseId: response.checkAcknowledgeResponse.caseId,
      taxId: response.checkAcknowledgeResponse.taxId,
      submitCreateCaseId: response.checkAcknowledgeResponse.sbmtCreateSLOAaceCaseID ?? '',
      status: { code: response.checkAcknowledgeResponse.statusCode, description: response.checkAcknowledgeResponse.statusDescription },
      requestedDate: response.checkAcknowledgeResponse.requestedDate,
      requestedTime: response.checkAcknowledgeResponse.requestedTime,
      source: response.checkAcknowledgeResponse.sourceDescription,
      loginId: response.checkAcknowledgeResponse.loginId,
      isCreatePdfFlow: false,
    };
  }),
  on(CheckActions.inquiryCallSuccessful,
    StandingAuthCheckActions.inquiryCallSuccessful, (state, response) => {
    return {
      ...state,
      initiatedBy: response.checkInquiryResponse?.initiatedBy ?? ''
    };
  }),
  on(ReviewPageActions.loadConfirmationPageWithoutAcknowledgeCall, (state, action) => {
    return {
      ...state,
      status: { code: '', description: 'Awaiting signed authorization'},
      requestedDate: DateTime.now().toFormat('MM/dd/yyyy'),
      requestedTime: getEasternTimeWithEtSuffix(null),
      source: 'schwabadvisorcenter.com',
      isCreatePdfFlow: action.isCondensedWireCreatePdfFlow
    }
  }),
  on(ConfirmationPageActions.viewTransactionStatusClicked, (state) => {
    return {
      ...state,
      viewTransactionStatusClicked: true
    }
  }),
  on(ConfirmationPageActions.createAnotherRequestClicked, (state) => {
    return {
      ...state,
      createAnotherRequestClicked: true
    }
  }),
  on(StandingAuthConfirmationPageActions.createNewStandingAuthClicked, (state) => {
    return {
      ...state,
      createNewStandingAuthClicked: true
    }
  }),
  on(ReviewPageActions.clearAttentionMessage, (state, _) => {
    return {
      ...state,
      achErrorList: []
    };
  }),
);
